import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityLogoutProps {
  onLogout: () => void;
  timeoutMinutes?: number;
  isAuthenticated: boolean;
}

/**
 * Custom hook that automatically logs out users after a period of inactivity
 * Detects mouse movements, keyboard activity, and touch events
 */
export const useInactivityLogout = ({
  onLogout,
  timeoutMinutes = 60,
  isAuthenticated,
}: UseInactivityLogoutProps): null => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutDuration = timeoutMinutes * 60 * 1000; // Convert to milliseconds

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only set timer if user is authenticated
    if (isAuthenticated) {
      timeoutRef.current = setTimeout(() => {
        console.log('⏱️ Inactivity timeout reached - logging out admin user');
        onLogout();
      }, timeoutDuration);
    }
  }, [onLogout, timeoutDuration, isAuthenticated]);

  useEffect(() => {
    // Don't track activity if user is not authenticated
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any user activity
    const handleActivity = () => {
      resetTimer();
    };

    // Initial timer setup
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, resetTimer]);

  return null;
};
